import { useEffect, useState } from 'react'
import { MapPin, RefreshCw, Repeat, UserCheck } from 'lucide-react'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { AsyncMultiSelect, FilterBar, MultiSelect, SearchInput } from '@/shared/ui/filters'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Forbidden, LoadingRows, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { listUnits } from '@/features/geography/api'
import { fmtDate, fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { AdoptionBatchResponse, CoverageRow } from '@/shared/api/types'
import { adoptStations, coverage, listMobilizers, reassignStations, stationAdoption } from './api'
import { AdoptionChip, CoverageChip, roleLabel } from './vocab'

/**
 * Adopt-a-station.
 *
 * ## Bulk is the shape, not a convenience
 *
 * A ward coordinator takes thirty stations at once, and one of them will already
 * belong to a colleague who got there first. The server commits the
 * twenty-nine and returns the refusal as *data*, naming the current owner — so
 * this screen reports a per-station result rather than an all-or-nothing error.
 * That is a deliberate departure from the rest of the app, and the result panel
 * says which stations went and which did not.
 *
 * ## The conflict names a person
 *
 * "Conflict" alone makes the user go and look, and they are the one who most
 * needs the answer. Every refusal carries the current adopter's name — not their
 * phone, which is PII and has no business on a coverage screen.
 */
export function MobilizerAdoptionPage() {
  const canRead = useCan('mobilizer:read')
  const canAdopt = useCan('mobilizer:adopt')

  const [q, setQ] = useState('')
  const [statuses, setStatuses] = useState<string[]>([])
  const [root, setRoot] = useState<{ value: string; label: string }[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [assigning, setAssigning] = useState<null | 'adopt' | 'reassign'>(null)
  const [inspect, setInspect] = useState<string | null>(null)
  const [version, setVersion] = useState(0)

  const rootId = root[0]?.value

  // Ticking thirty stations in one ward, narrowing to another, and pressing
  // Assign would otherwise adopt thirty stations that are not on screen. The
  // selection belongs to the list that produced it.
  useEffect(() => { setSelected(new Set()) }, [q, rootId, statuses.join(',')])

  const data = useLoad(
    () => (canRead
      ? coverage({
          level: 'POLLING_STATION',
          q: q || undefined,
          unitId: rootId,
          status: statuses.length ? statuses : undefined,
        })
      : Promise.resolve(null)),
    [canRead, q, rootId, statuses.join(','), version])

  if (!canRead) return <Forbidden what="station adoption" />

  const rows: CoverageRow[] = data.data?.rows ?? []
  const t = data.data?.totals
  const unowned = rows.filter((r) => !r.adopterId)
  const activeCount = [q, rootId].filter(Boolean).length + (statuses.length ? 1 : 0)

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Adopt a station"
        description="Adoption is ownership, not assignment. Exactly one person is answerable for each station, and the database enforces it rather than this screen."
        right={(
          <div className="flex gap-1.5">
            <button type="button" className="erp-btn" aria-label="Refresh"
                    onClick={() => setVersion((v) => v + 1)}>
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            {canAdopt && (
              <>
                <button type="button" className="erp-btn" disabled={selected.size === 0}
                        onClick={() => setAssigning('reassign')}>
                  <Repeat className="h-3.5 w-3.5" /> Reassign
                </button>
                <button type="button" className="erp-btn-primary" disabled={selected.size === 0}
                        onClick={() => setAssigning('adopt')}>
                  <UserCheck className="h-3.5 w-3.5" /> Assign {selected.size || ''}
                </button>
              </>
            )}
          </div>
        )}
      />

      <KpiStrip>
        <MetricCard label="Stations" value={fmtNumber(t?.stations ?? 0)} sub="in this scope" accent />
        <MetricCard label="With an owner" value={fmtNumber(t?.stationsAdopted ?? 0)}
                    sub={`${fmtNumber((t?.stations ?? 0) - (t?.stationsAdopted ?? 0))} without`} />
        <MetricCard label="Voters covered" value={fmtNumber(t?.votersCovered ?? 0)}
                    sub={`of ${fmtNumber(t?.voters ?? 0)} — the number that matters more than the count`} />
        <MetricCard label="Selected" value={fmtNumber(selected.size)}
                    sub={selected.size ? 'ready to assign or reassign' : 'tick rows to act on them'} />
      </KpiStrip>

      <FilterBar activeCount={activeCount}>
        <SearchInput value={q} onChange={setQ} placeholder="Search station name" />
        <MultiSelect
          label="Coverage"
          options={[
            { value: 'GAP', label: 'No mobilizer' },
            { value: 'UNDERSTAFFED', label: 'Understaffed' },
            { value: 'OK', label: 'Healthy' },
            { value: 'OVERSTAFFED', label: 'Overstaffed' },
          ]}
          selected={statuses}
          onChange={setStatuses}
        />
        <AsyncMultiSelect
          label="Within"
          selected={root}
          onChange={(next) => setRoot(next.slice(-1))}
          placeholder="Narrow to a county, constituency or ward"
          search={async (term) => {
            const found = await listUnits({ q: term } as never, 0, 10)
            return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
          }}
        />
      </FilterBar>

      <ErrorStrip message={data.error} />

      {unowned.length > 0 && (
        <Note tone="info">
          {unowned.length} of these stations have no owner. Ticking them and assigning is
          how that changes — and a station somebody else already owns will come back named,
          not silently skipped.
        </Note>
      )}

      <Panel bodyClassName="p-0">
        <ErpTable head={<>
          {canAdopt && (
            <Th className="w-10">
              <input
                type="checkbox"
                aria-label="Select every station on this page"
                checked={rows.length > 0 && selected.size === rows.length}
                onChange={(e) => setSelected(e.target.checked
                  ? new Set(rows.map((r) => r.unitId)) : new Set())}
              />
            </Th>
          )}
          <Th>Station</Th>
          <Th>Owner</Th>
          <Th className="text-right">Voters</Th>
          <Th className="text-right">Mobilizers</Th>
          <Th>Status</Th>
          <Th className="w-20">History</Th>
        </>}>
          {data.loading && !data.data ? (
            <LoadingRows colSpan={canAdopt ? 7 : 6} label="Loading stations…" />
          ) : rows.length === 0 ? (
            <EmptyRow colSpan={canAdopt ? 7 : 6} message="No stations match these filters." />
          ) : rows.map((r) => (
            <tr key={r.unitId} className="hover:bg-muted/40" data-station={r.unitName ?? r.unitId}>
              {canAdopt && (
                <Td>
                  <input
                    type="checkbox"
                    aria-label={`Select ${r.unitName ?? 'station'}`}
                    checked={selected.has(r.unitId)}
                    onChange={() => toggle(r.unitId)}
                  />
                </Td>
              )}
              <Td>
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="font-medium truncate">{r.unitName ?? r.unitId}</span>
                </span>
              </Td>
              <Td>
                {r.adopterName ?? <span className="text-muted-foreground">Nobody</span>}
              </Td>
              <Td className="text-right erp-metric">{fmtNumber(r.voters)}</Td>
              <Td className="text-right erp-metric">{fmtNumber(r.mobilizers)}</Td>
              <Td><CoverageChip code={r.status} /></Td>
              <Td>
                <button type="button" className="erp-btn" onClick={() => setInspect(r.unitId)}>
                  View
                </button>
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>

      <AssignDialog
        mode={assigning}
        stationIds={[...selected]}
        onClose={() => setAssigning(null)}
        onDone={() => { setAssigning(null); setSelected(new Set()); setVersion((v) => v + 1) }}
      />

      <StationHistoryDialog stationId={inspect} onClose={() => setInspect(null)} />
    </div>
  )
}

function AssignDialog({
  mode, stationIds, onClose, onDone,
}: {
  mode: null | 'adopt' | 'reassign'
  stationIds: string[]
  onClose: () => void
  onDone: () => void
}) {
  const [who, setWho] = useState<{ value: string; label: string }[]>([])
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AdoptionBatchResponse | null>(null)

  const run = async () => {
    const target = who[0]?.value
    if (!target) return
    setBusy(true); setError(null)
    try {
      const res = mode === 'reassign'
        ? await reassignStations(stationIds, target, reason.trim() || undefined)
        : await adoptStations(target, stationIds, reason.trim() || undefined)
      setResult(res)
    } catch (err) { setError(explain(err)) } finally { setBusy(false) }
  }

  const close = () => { setWho([]); setReason(''); setResult(null); setError(null); onClose() }

  return (
    <Modal
      open={mode !== null}
      onClose={close}
      title={mode === 'reassign'
        ? `Reassign ${stationIds.length} station${stationIds.length === 1 ? '' : 's'}`
        : `Assign ${stationIds.length} station${stationIds.length === 1 ? '' : 's'}`}
      description={mode === 'reassign'
        ? 'The current owner is superseded, and the row that replaces theirs points back at it — so who took what from whom stays answerable.'
        : 'A station somebody already owns comes back refused, naming them. The rest are committed.'}
      footer={result ? (
        <button type="button" className="erp-btn-primary" onClick={() => { close(); onDone() }}>
          Done
        </button>
      ) : (
        <>
          <button type="button" className="erp-btn" onClick={close}>Cancel</button>
          <button type="button" className="erp-btn-primary" disabled={busy || who.length !== 1}
                  onClick={() => void run()}>
            {busy ? 'Working…' : mode === 'reassign' ? 'Reassign' : 'Assign'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        {result ? (
          <>
            <Note tone={result.refused === 0 ? 'good' : 'info'}>
              {result.adopted} of {result.requested} assigned
              {result.refused > 0 ? `, ${result.refused} refused — the successful ones are committed.` : '.'}
            </Note>
            {result.refused > 0 && (
              <ul className="erp-card divide-y divide-border/60">
                {result.results.filter((r) => !r.adopted).map((r) => (
                  <li key={r.stationId} className="px-3 py-2">
                    <span className="block text-erp-sm font-medium">{r.stationName ?? r.stationId}</span>
                    <span className="block text-erp-xs text-muted-foreground">
                      {r.currentMobilizerName
                        ? `Already owned by ${r.currentMobilizerName}.`
                        : r.reasonCode === 'NOT_A_STATION'
                          ? 'That unit is not a polling station.'
                          : r.reasonCode === 'ALREADY_YOURS'
                            ? 'They already own it.'
                            : 'Refused.'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <AsyncMultiSelect
              label="Mobilizer"
              selected={who}
              onChange={(next) => setWho(next.slice(-1))}
              placeholder="Search by name"
              search={async (term) => {
                const page = await listMobilizers({ q: term, status: 'ACTIVE' }, undefined, 25)
                return page.content.map((m) => ({
                  value: m.id,
                  label: m.fullName,
                  sub: `${roleLabel(m.roleCode)} · ${m.primaryUnitName ?? 'no unit'}`,
                }))
              }}
            />
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">
                Note (optional — kept on the record)
              </span>
              <input className="erp-input w-full" value={reason}
                     onChange={(e) => setReason(e.target.value)}
                     placeholder="Ward review, August" />
            </label>
          </>
        )}
      </div>
    </Modal>
  )
}

/** Who owns this station now, and who owned it before — one call, one panel. */
function StationHistoryDialog({
  stationId, onClose,
}: { stationId: string | null; onClose: () => void }) {
  const load = useLoad(
    () => (stationId ? stationAdoption(stationId) : Promise.resolve(null)), [stationId])
  const d = load.data

  return (
    <Modal
      open={stationId !== null}
      onClose={onClose}
      title={d?.stationName ?? 'Station'}
      description="Rows are never deleted or rewritten. Releasing sets a date; reassigning points the old row at the one that replaced it."
      footer={<button type="button" className="erp-btn-primary" onClick={onClose}>Close</button>}
    >
      <div className="space-y-3">
        <ErrorStrip message={load.error} />
        {load.loading && !d ? (
          <p className="text-erp-sm text-muted-foreground">Loading…</p>
        ) : !d ? null : !d.current && d.history.length === 0 ? (
          <p className="text-erp-sm text-muted-foreground">
            Nobody has ever owned this station. That is a gap, not an error.
          </p>
        ) : (
          <>
          {/* `current` is its own field on the response, not necessarily the head
              of `history`. Reading only the list would make an owned station
              render as unowned the moment the server stops repeating the live row
              in it. */}
          {d.current && (
            <div className="erp-card px-3 py-2">
              <span className="block text-erp-xs text-muted-foreground">Owner now</span>
              <span className="block text-erp-sm font-medium">
                {d.current.mobilizerName ?? d.current.mobilizerId}
              </span>
              <span className="block text-erp-xs text-muted-foreground">
                Since {fmtDate(d.current.adoptedAt)}
              </span>
            </div>
          )}
          <ul className="erp-card divide-y divide-border/60">
            {d.history.map((a) => (
              <li key={a.id} className="px-3 py-2 flex items-start gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-erp-sm font-medium truncate">
                    {a.mobilizerName ?? a.mobilizerId}
                  </span>
                  <span className="block text-erp-xs text-muted-foreground">
                    {fmtDate(a.adoptedAt)}
                    {a.releasedAt ? ` → ${fmtDate(a.releasedAt)}` : ' → now'}
                    {a.releaseReason ? ` · ${a.releaseReason}` : ''}
                  </span>
                </span>
                <AdoptionChip code={a.statusCode} />
              </li>
            ))}
          </ul>
          </>
        )}
      </div>
    </Modal>
  )
}
